<script setup lang="ts">
import {
  computed,
  CSSProperties,
  nextTick,
  onMounted,
  onUnmounted,
  PropType,
  ref,
  VNode,
  watch,
} from "vue";
import { MenuEntry } from "@/models/menu-entry";
import { DesignVersion, Placement } from "@/models/types";
import BcMenuEntry from "./bc-menu-entry.vue";

defineOptions({ name: "BcMenu" });

const emit = defineEmits<{
  (e: "click", entry: MenuEntry): void;
}>();

const pak = computed<string>(() => {
  return "bc-controls-d" + (props.design == "6" ? "6" : "7");
});
const menuRef = ref<HTMLDivElement>();
const menuStyle = ref<CSSProperties>({ display: "none" });
const subMenuStyle = ref<CSSProperties>({});
const width = ref(250);
const subWidth = ref(500);
const itemHeight = ref(34);

const props = defineProps({
  x: { type: Number, default: 0 },
  y: { type: Number, default: 0 },
  items: { type: Array<MenuEntry>, default: () => new Array<MenuEntry>() },
  isVisible: { type: Boolean, default: true },
  separateAll: { type: Boolean, default: false },
  /* Only works whene there are no sub-items in props.items */
  numberOfVisibleItems: { type: Number },
  design: {
    type: String as PropType<DesignVersion>,
    default: "7new",
    validator: (value: any) => DesignVersion.isDesignVersion(value),
  },
  disableHeightCalculation: { type: Boolean, default: false },
  /* Used for bc-popup-menu position calulation */
  parentRect: { type: Object as PropType<HTMLDivElement> },
  /** Specifies the placement of the menu items compared to the container element */
  placement: {
    type: String as PropType<Placement>,
    default: "bottom-start",
    validator: (value: any) => Placement.isPlacement(value),
  },
  fullWidth: { type: Boolean, default: false },
  textWrap: { type: Boolean, default: false },
  subItemIcon: { type: Object as PropType<VNode> },
  selectedItem: { type: Object as PropType<MenuEntry> },
  offset: { type: Number, default: 0 },
  teleportTarget: { type: String },
});

onMounted(async () => {
  document.addEventListener("scroll", update);
  window.addEventListener("resize", update);
  if (props.isVisible) {
    await calculateMenuStyle();
  }

  if (props.teleportTarget && menuRef?.value) {
    const target = document.querySelector(props.teleportTarget);
    target?.appendChild(menuRef.value);
  }
});

onUnmounted(() => {
  document.removeEventListener("scroll", update);
  window.removeEventListener("resize", update);
});

const hasSubItems = computed<boolean>(() => {
  return (menuRef.value?.getElementsByClassName("subMenuControl")?.length ?? 0) > 0;
});

let numberOfSeparators = computed<number>(() =>
  props.separateAll
    ? props.items.length - 1
    : props.items.filter((x) => x.hasSeparator).length
);
const menuHeight = computed<number>(() => {
  return props.numberOfVisibleItems &&
    props.numberOfVisibleItems > 0 &&
    !hasSubItems.value
    ? props.numberOfVisibleItems * itemHeight.value + props.numberOfVisibleItems
    : menuRef?.value?.offsetHeight ??
        props.items.length * itemHeight.value + numberOfSeparators.value;
});

function calcItemHeight() {
  const firstItem = menuRef?.value?.getElementsByClassName(
    "menuItem"
  )[0] as HTMLDivElement;
  if (!firstItem) {
    itemHeight.value = 34;
    return;
  }

  const firstItemRect = firstItem.getBoundingClientRect();
  const firstItemStyle = window.getComputedStyle(firstItem);
  const height =
    firstItemRect.height +
    (parseFloat(firstItemStyle.marginTop) ?? 0) +
    (parseFloat(firstItemStyle.marginBottom) ?? 0);

  itemHeight.value = height ?? 34;
}

function update() {
  if (!props.isVisible) return;

  requestAnimationFrame(calculateMenuStyle);
}

function calcFixedPosition() {
  const position = [props.x, props.y];
  const posX = Math.max(0, Math.min(window.innerWidth - width.value, position[0]));
  const posY = Math.max(0, Math.min(window.innerHeight - menuHeight.value, position[1]));
  menuStyle.value.left = `${posX}px`;
  menuStyle.value.top = `${posY}px`;
}

async function calcPlacementPosition() {
  const parentRect = props.parentRect!.getBoundingClientRect();

  if (props.fullWidth || props.textWrap) {
    menuStyle.value["min-width"] = parentRect.width + "px";
  }

  let height = menuHeight.value;
  if (!props.disableHeightCalculation && !hasSubItems.value) {
    const maxHeight = props.placement.startsWith("bottom")
      ? window.innerHeight - parentRect.bottom
      : parentRect.top;
    height = Math.min(maxHeight, menuHeight.value);
    menuStyle.value.maxHeight = height + "px";
    menuStyle.value.overflowY = "auto";
  }

  await nextTick();
  width.value = menuRef.value?.offsetWidth ?? width.value;

  if (props.placement.startsWith("bottom") || props.placement.startsWith("top")) {
    if (props.placement.startsWith("bottom"))
      menuStyle.value.top = parentRect.bottom + props.offset + "px";
    else menuStyle.value.top = parentRect.top - height - props.offset + "px";

    if (props.placement.endsWith("start")) menuStyle.value.left = parentRect.left + "px";
    else if (props.placement.endsWith("end"))
      menuStyle.value.left = parentRect.right - width.value + "px";
    else
      menuStyle.value.left =
        parentRect.right - parentRect.width / 2 - width.value / 2 + "px";
  } else if (props.placement.startsWith("left") || props.placement.startsWith("right")) {
    if (props.placement.startsWith("left"))
      menuStyle.value.left = parentRect.left - width.value - props.offset + "px";
    else menuStyle.value.left = parentRect.right + props.offset + "px";

    if (props.placement.endsWith("start")) menuStyle.value.top = parentRect.top + "px";
    else if (props.placement.endsWith("end"))
      menuStyle.value.top = parentRect.bottom - height + "px";
    else menuStyle.value.top = parentRect.top + parentRect.height / 2 - height / 2 + "px";
  }

  return;
}

async function calculateMenuStyle() {
  if (!menuRef.value || menuRef.value.offsetWidth == 0) {
    menuStyle.value = {
      display: undefined,
      opacity: 0,
    };
    await nextTick();
    calcItemHeight();
    width.value = menuRef.value?.offsetWidth ?? width.value;
  }

  const hasNumberOfVisibleItems =
    props.numberOfVisibleItems &&
    props.numberOfVisibleItems > 0 &&
    props.items.findIndex((i) => i.subItems?.length ?? 0 > 0) == -1;

  menuStyle.value = {
    display: props.isVisible ? undefined : "none",
  };

  if (!props.isVisible) return;

  if (!props.disableHeightCalculation && hasNumberOfVisibleItems && !hasSubItems.value) {
    menuStyle.value.maxHeight = menuHeight.value + "px";
    menuStyle.value.overflowY = "auto";
  }

  if (props.parentRect) {
    await calcPlacementPosition();
  } else {
    calcFixedPosition();
  }

  if (hasNumberOfVisibleItems) {
    _scrollToSelectedItem();
  }
}

function openSubMenu(item: MenuEntry, itemIdx: number) {
  if (!item.subItems) {
    subMenuStyle.value = {};
    return;
  }

  _updateSubMenuWidth();
  const menuRect = menuRef.value!.getBoundingClientRect();
  const positionX = Math.max(0, Math.min(window.innerWidth - width.value, menuRect.x));
  const positionY = Math.max(
    0,
    Math.min(
      window.innerHeight -
        (props.items.length * itemHeight.value + numberOfSeparators.value),
      menuRect.y
    )
  );

  const swapSubItemsHorizontal =
    window.innerWidth - (width.value + subWidth.value) < positionX;
  const swapSubItemsVertical = _shouldOpenUpwards(positionY, itemIdx, item.subItems);
  subMenuStyle.value = {
    ...(swapSubItemsHorizontal ? { right: "99%" } : { left: "99%" }),
    ...(swapSubItemsVertical ? { bottom: 0 } : { top: 0 }),
  };
}

function _shouldOpenUpwards(
  positionY: number,
  itemIndex: number,
  subActions: Array<MenuEntry>
): boolean {
  const numberOfSeparators = props.items.filter(
    (x, ind) => ind < itemIndex && (props.separateAll || x.hasSeparator)
  ).length;
  const actionTop = positionY + itemIndex * itemHeight.value + numberOfSeparators + 2;

  //Check if we can open down
  const subActionsBottom = actionTop + subActions.length * itemHeight.value;
  if (subActionsBottom < window.innerHeight) {
    return false;
  }

  //IF not, check in which direction ther is more space
  const actionBottom = actionTop + itemHeight.value;
  if (actionTop > window.innerHeight - actionBottom) {
    return true;
  } else {
    return false;
  }
}

function _updateSubMenuWidth() {
  if (!menuRef.value) return;

  let subContextMenuControls = menuRef.value.getElementsByClassName("subMenuControl");
  for (var i = 0; i < subContextMenuControls.length; i++) {
    if (subContextMenuControls[i].checkVisibility()) {
      subWidth.value = (subContextMenuControls[i] as HTMLDivElement).offsetWidth;
      break;
    }
  }
}

function _scrollToSelectedItem() {
  setTimeout(() => {
    const selectedMenuItem = menuRef.value?.getElementsByClassName("selected");
    if (selectedMenuItem && selectedMenuItem.length > 0) {
      selectedMenuItem[0].scrollIntoView({ block: "nearest" });
    } else if (menuRef.value?.getElementsByClassName("menuItem").length ?? 0 > 0) {
      menuRef.value?.getElementsByClassName("menuItem")[0].scrollIntoView();
    }
  }, 0);
}

watch(props, calculateMenuStyle);

defineExpose({
  update,
  menuStyle: menuStyle.value,
});
</script>

<template>
  <div
    class="bissantzControls menuControl"
    ref="menuRef"
    :data-pak="pak"
    :data-design="design"
    :style="menuStyle"
  >
    <slot>
      <template v-for="(item, itemIdx) in items" :key="item.key">
        <BcMenuEntry
          v-if="item.component || item.text"
          :item="item"
          :subItemIcon="subItemIcon"
          :hasSeparator="(separateAll || item.hasSeparator) && itemIdx < items.length - 1"
          :selected="
            !!selectedItem &&
            selectedItem.key == item.key &&
            selectedItem.text == item.text
          "
          :textWrap="textWrap"
          :hasIconPlaceHolder="!!items.find((i) => !!i.icon)"
          @click="emit('click', $event)"
          @mouseenter="openSubMenu(item, itemIdx)"
        >
          <template v-slot:before-item>
            <slot name="before-item" :entry="item" :entryIdx="itemIdx" />
          </template>
          <template v-slot:subMenu>
            <div
              class="bissantzControls menuControl subMenuControl"
              v-if="item.subItems && item.subItems.length"
              :data-pak="pak"
              :data-design="design"
              :style="subMenuStyle"
            >
              <template v-for="(subItem, subItemIdx) in item.subItems" :key="subItem.key">
                <BcMenuEntry
                  v-if="subItem.component || subItem.text"
                  :item="subItem"
                  :hasSeparator="
                    (separateAll || subItem.hasSeparator) &&
                    subItemIdx < item.subItems.length - 1
                  "
                  @click="emit('click', $event)"
                />
              </template>
            </div>
          </template>
        </BcMenuEntry>
      </template>
    </slot>
  </div>
</template>

<style lang="less" scoped>
@import "../../styles/components/menu/bc-menu.less";
</style>
